import React from "react";
import { trackQuote } from "@/lib/track";
import { scrollToId } from "@/lib/scrollToId";
import {
  MapPin,
  Building2,
  Home,
  Warehouse,
  Tractor,
  ShoppingBag,
  ShieldCheck,
  Check,
  ExternalLink,
} from "lucide-react";

export const ServiceAreas: React.FC = () => {
  const primaryAreas = [
    { name: "Exeter", highlight: "Hub & Headquarters" },
    { name: "Exmouth", highlight: "Coastal & Residential" },
    { name: "Newton Abbot", highlight: "Commercial & Self-Build" },
    { name: "Torquay", highlight: "Torbay Coverage" },
    { name: "Tiverton", highlight: "Mid Devon Projects" },
    { name: "Honiton", highlight: "East Devon & UFH" },
    { name: "Taunton", highlight: "Somerset Border" },
    { name: "Topsham", highlight: "Renovations & Extensions" },
    { name: "Plymouth", highlight: "South Devon & Waterfront" },
    { name: "Barnstaple", highlight: "North Devon Coverage" },
    { name: "Bath", highlight: "Somerset & Heritage" },
    { name: "Bournemouth", highlight: "Dorset Coast" },
  ];

  const sectors = [
    {
      title: "New Builds & Self-Builds",
      icon: Home,
      desc: "Perfectly flat floors that work brilliantly with modern underfloor heating in new homes.",
    },
    {
      title: "Extensions & Renovations",
      icon: Building2,
      desc: "Quick-drying screed that's ideal for extensions and renovation projects where space is tight.",
    },
    {
      title: "Garage & Driveway Floors",
      icon: ShieldCheck,
      desc: "Tough, hard-wearing screed that can take the weight of vehicles and heavy use.",
    },
    {
      title: "Shops, Offices & Warehouses",
      icon: Warehouse,
      desc: "Flat, durable floors for shops, offices, and storage spaces built to handle daily wear.",
    },
    {
      title: "Farm & Equestrian Buildings",
      icon: Tractor,
      desc: "Moisture-resistant screed that stands up to the demands of farm and stable buildings.",
    },
    {
      title: "Care Homes & Holiday Lets",
      icon: ShoppingBag,
      desc: "Warm, quiet floors that keep guests and residents comfortable and energy bills low.",
    },
  ];

  return (
    <section
      id="service-area"
      className="py-20 bg-[#080810] text-white border-b border-stone-800/80 relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs sm:text-sm uppercase tracking-widest text-[#D4AF37] font-semibold block mb-2">
            AREAS WE COVER
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
            Liquid Screed Across <br className="hidden sm:inline" />
            <span className="gold-gradient-text">Devon, Somerset & Dorset</span>
          </h2>
          <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
            We provide liquid screed and floor preparation across Devon,
            Somerset and the surrounding South West regions.
          </p>
        </div>

        {/* Embedded Map */}
        <div className="mb-16 bg-[#12121D] border border-stone-800/80 rounded-xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
            <div>
              <h3 className="text-xl font-bold text-white">Our Service Area</h3>
              <p className="text-stone-400 text-xs">
                We cover South West England and surrounding areas with fast,
                reliable service.
              </p>
            </div>
            <a
              href="https://maps.google.com/?cid=17672970897147079334"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] font-bold hover:underline"
            >
              <span>Open in Google Maps</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="relative w-full h-80 sm:h-96 rounded-lg overflow-hidden border border-stone-800/80">
            <iframe
              title="Exeter Flow Screeds Service Area Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d636594.1378876251!2d-3.85827365!3d50.73812735!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x486d0ab8fb09ff7b%3A0x10b759ed6e50284e!2sDevon%2C%20UK!5e0!3m2!1sen!2suk!4v1710000000000!5m2!1sen!2suk"
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>

        {/* Primary Areas Grid */}
        <div className="mb-16">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#D4AF37]" />
            <span>Key Service Areas in South West England</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {primaryAreas.map((area) => (
              <a
                key={area.name}
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  trackQuote(
                    "service_area_" +
                      area.name.toLowerCase().replace(/\s/g, "_"),
                  );
                  scrollToId("#contact");
                }}
                className="bg-[#12121D] border border-stone-800/80 hover:border-[#D4AF37] rounded-xl p-4 transition-all hover:-translate-y-0.5 group shadow-sm"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-white group-hover:text-[#D4AF37] transition-colors">
                    {area.name}
                  </span>
                  <Check className="w-4 h-4 text-[#D4AF37] opacity-80" />
                </div>
                <span className="text-xs text-stone-400 block">
                  {area.highlight}
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* Sectors Breakdown */}
        <div>
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#D4AF37]" />
            <span>Types of Work We Do</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sectors.map((sector) => {
              const IconComp = sector.icon;
              return (
                <div
                  key={sector.title}
                  className="bg-[#12121D] border border-stone-800/80 rounded-xl p-6 hover:border-[#D4AF37]/50 transition-colors shadow-sm"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center text-[#D4AF37] mb-4 border border-[#D4AF37]/20">
                    <IconComp className="w-5 h-5" />
                  </div>
                  <h4 className="text-white font-bold text-base mb-2">
                    {sector.title}
                  </h4>
                  <p className="text-stone-400 text-xs sm:text-sm leading-relaxed">
                    {sector.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
